// src/hooks/useDecompScene2050ByScenario.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const DECOMP_SCENE = gql`
  query DecompScene($scenario: String, $from: Int!, $to: Int!) {
    timeSeries(view: DECOMP_SCENE, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      group
      value
      scenario
    }
  }
`;

// --- helpers ---------------------------------------------------------------

// Round shares to whole % that sum to 100 (largest fractions get the +1s)
function wholePercentsThatSumTo100(rawShares = []) {
  const floors = rawShares.map(Math.floor);
  let need = 100 - floors.reduce((a, b) => a + b, 0);
  if (need <= 0) return floors;
  const frac = rawShares
    .map((v, i) => ({ i, f: v - Math.floor(v) }))
    .sort((a, b) => b.f - a.f);
  for (let k = 0; k < need && k < frac.length; k++) floors[frac[k].i] += 1;
  return floors;
}

// Trapezoidal integral over irregular steps.
// points: array of [year, value] (unsorted, may have duplicates).
function trapezoidIntegral(points) {
  if (!points?.length) return 0;
  // collapse duplicates per year first
  const byYear = new Map();
  for (const [y, v] of points) byYear.set(y, (byYear.get(y) || 0) + (Number(v) || 0));
  const yrs = Array.from(byYear.keys()).sort((a, b) => a - b);
  if (yrs.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < yrs.length - 1; i++) {
    const y0 = yrs[i], y1 = yrs[i + 1];
    const v0 = byYear.get(y0), v1 = byYear.get(y1);
    total += 0.5 * (v0 + v1) * (y1 - y0); // area of each trapezoid
  }
  return total;
}

// Ensure endpoints exist; if missing, assume 0 (matches Steve's sheet when 2025 row is blank/zero)
function withEndpoints(points, start, end) {
  const have = new Set(points.map(([y]) => y));
  const out = points.slice();
  if (!have.has(start)) out.push([start, 0]);
  if (!have.has(end))   out.push([end, 0]);
  return out;
}

// --- hook ------------------------------------------------------------------

/**
 * Cumulative net-balance decomposition (2025–2050 by default).
 * - Integrates each wedge over [start..end] using trapezoids
 * - Returns signed totals, absolute totals, and positive % shares
 * - Optional whole % rounding to match “professor numbers”
 */
export function useDecompScene2050ByScenario({
  scenario,
  start = 2025,
  end = 2050,
  roundToWholePercent = false, // true => whole % that sum to 100
} = {}) {
  const { data, loading, error } = useQuery(DECOMP_SCENE, {
    variables: { scenario, from: start, to: end },
    fetchPolicy: "cache-first",
    skip: !scenario,
  });

  const {
    byWedge,             // signed integrals per wedge
    byWedgePositive,     // absolute integrals
    percentByWedge,      // positive % shares (sum to 100)
    wedges,              // wedge names ordered by descending %
    label,               // "2025–2050"
  } = useMemo(() => {
    const rows = data?.timeSeries ?? [];

    // Group rows per wedge -> [[year, value], ...]
    const perWedge = new Map(); // wedge -> Array<[year, value]>
    for (const r of rows) {
      const w = String(r.group || "Other");
      const y = Number(r.year);
      const v = Number(r.value) || 0;
      if (!Number.isFinite(y)) continue;
      if (!perWedge.has(w)) perWedge.set(w, []);
      perWedge.get(w).push([y, v]);
    }

    // Trapezoidal integral per wedge over [start..end]
    const signed = new Map();   // wedge -> signed integral
    for (const [w, pts] of perWedge.entries()) {
      const clipped = pts.filter(([y]) => y >= start && y <= end);
      const withEnds = withEndpoints(clipped, start, end);
      const area = trapezoidIntegral(withEnds);
      signed.set(w, area);
    }

    // Absolute totals and shares (positive-only)
    const positive = new Map();
    for (const [w, v] of signed.entries()) positive.set(w, Math.abs(v));

    const totalPos = Array.from(positive.values()).reduce((a, b) => a + b, 0);

    // Build percent map; optionally snap to whole %
    let pctEntries = Array.from(positive.entries()).map(([w, v]) => [
      w,
      totalPos ? (v / totalPos) * 100 : 0,
    ]);

    if (roundToWholePercent && pctEntries.length > 0) {
      const ordered = pctEntries.slice().sort((a, b) => b[1] - a[1]); // deterministic rounding
      const rounded = wholePercentsThatSumTo100(ordered.map(([, p]) => p));
      pctEntries = ordered.map(([w], i) => [w, rounded[i]]);
    }

    // Order wedges by descending percent
    const wedgesOrdered = pctEntries
      .slice()
      .sort((a, b) => b[1] - a[1])
      .map(([w]) => w);

    // maps to plain objects
    const toObj = (m) => Object.fromEntries(m instanceof Map ? m.entries() : m);

    return {
      byWedge: toObj(signed),
      byWedgePositive: toObj(positive),
      percentByWedge: Object.fromEntries(pctEntries),
      wedges: wedgesOrdered,
      label: `${start}–${end}`,
    };
  }, [data, start, end, roundToWholePercent]);

  return {
    year: label, // back-compat: consumer expects `year`
    wedges,
    byWedge,
    byWedgePositive,
    percentByWedge,
    loading,
    error,
  };
}

// keep your existing labelForScenarioKey export if you need it elsewhere
export function labelForScenarioKey(key = "") {
  const k = key.toLowerCase();
  const isHigh = /(?:^|_)high(?:_|$)/.test(k);
  const isLow  = /(?:^|_)low(?:_|$)/.test(k);
  const ambition = isHigh ? "More Change" : isLow ? "Less Change" : "";
  let name = "Current";
  if (/(?:^|_)steer(?:_|$)/.test(k)) name = "Steer";
  else if (/(?:^|_)shift(?:_|$)/.test(k)) name = "Shift";
  else if (/(?:^|_)transform(?:_|$)/.test(k)) name = "Transform";
  return ambition ? `${ambition} + ${name}` : name;
}
